import { inject, Injectable } from '@angular/core';
import { CoreService, InMemoryCacheService, MessageType, UtilsService } from 'src/app/common';
import { firstValueFrom } from 'rxjs';
import { DbcModel, DbcPhone, DbcLink, DbcSocial, HistoryItem, OrderHistoryResponse, DbcProductCarousel, DefaultContactNotificationsConfig } from '../model/dbc-model';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AnalyticsApiService } from './analytics-api.service';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { CustomField, CustomFormConfig, Recipient } from '../model/custom-from.model';
import { CreateBusinessCardFromTemplateRequest, DigitalBusinessCardInputModel, EffectiveBusinessCardConfig } from '../model/org-business-card.model';
import { applyFieldRulesToForm, buildCreateFromTemplateRequest, isFieldLocked, sanitizeCardInputForRules } from '../utils/org-business-card-rules.util';

@Injectable({
  providedIn: 'root'
})
export class DbcService {
    private static readonly SMS_DESTINATION_REGEX = /^\+[1-9]\d{7,14}$/;

    public dbcForm: FormGroup;
    public orderForm: FormGroup;

    public isEditMode = false;
    public currentCardId: string | null = null;
    public currentProjectId: string | null = null;
    public currentEffectiveConfig: EffectiveBusinessCardConfig | null = null;
    public currentTeamId: string | null = null;

    private _dbcCards: { [key: string]: DbcModel[] } = {};
    private _products: any[] = []; // Store fetched products

    private core = inject(CoreService<{}>);
    private fb = inject(FormBuilder);
    private utils = inject(UtilsService);
    private cache = inject(InMemoryCacheService);
    private analyticsApiService = inject(AnalyticsApiService);
    private http = inject(HttpClient);

    private billingId: string | null = null;

    constructor() {
        this.billingId = this.cache.auth?.user?.user?.billingId || '';
        this.initForm();
        this.initOrderForm();
    }

    // Main form initialization method
    private initForm(): void {
        this.dbcForm = this.fb.group({
            id: [''],
            projectId: [''],
            billingId: [this.billingId],
            planName: [''],
            planType: [''],
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            userName: ['', Validators.required],
            email: ['', [Validators.email]],
            emailForWalletLinks: ['', [Validators.email]],
            logo: [''],
            photo: [''],
            appleWalletLogo: [''],
            phone: this.fb.array([]),
            organization: [''],
            description: [''],
            title: [''],
            links: this.fb.array([]),
            productCarousel: this.fb.array([]),
            social: this.fb.array([]),
            vcf: [''],
            qrCode: [''],
            role: [''],
            template: [''],
            primaryColor: [''],
            secondaryColor: [''],
            digitalCardTextColor: ['#ffffff'],
            buttonTextColor: ['#ffffff'],
            walletTextColor: ['#000000'],
            walletBackgroundColor: ['#ffffff'],
            includePoweredByUrl: [true],
            poweredBy: [''],
            poweredByUrl: ['', [Validators.pattern(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/)]],
            googleWalletSaveUrl: [''],
            appleWalletPkpassUrl: [''],
            backgroundImage: [''],
            backgroundImageOpacity: [0.8],
            customizeWallet: [false],
            brandedQr: [false],
            webhookEnabled: [false],
            webhookUrl: ['', [Validators.pattern(/^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/)]],
            customForm: this.fb.array([]),
            defaultContactNotifications: this.createDefaultContactNotificationsGroup(),
            orgId: [this.cache.user?.currentOrganizationId ?? ''],
            cardMode: [''],
            sourceTemplateId: [''],
            sourceTemplateVersion: [null],
            createdFromOrgTemplate: [false],
            teamId: [''],
            isDeleted: [false],
            ownerUserId: [this.resolveCurrentUserId()],
            isActive: [false],
        });
    }

    // Separate order form initialization method
    private initOrderForm(): void {
        this.orderForm = this.fb.group({
            quantity: [1, [Validators.required, Validators.min(1), Validators.max(1000)]],
            order: this.fb.array([]),
            frontDesign: this.fb.array([]),
            backDesign: this.fb.array([]),
            address: this.createBillingAddressFormGroup(),
            successUrl: [''],
            cancelUrl: [''],
            providerProductId: ['']
        });
    }

    // Custom Forms Getters
    get customForm(): FormArray {
        return this.dbcForm.get('customForm') as FormArray;
    }

    get defaultContactNotificationsGroup(): FormGroup {
        return this.dbcForm.get('defaultContactNotifications') as FormGroup;
    }

    get defaultContactRecipientsFormArray(): FormArray {
        return this.defaultContactNotificationsGroup.get('sendResultsTo') as FormArray;
    }

    get phoneFormArray(): FormArray {
        return this.dbcForm.get('phone') as FormArray;
    }

    get linksFormArray(): FormArray {
        return this.dbcForm.get('links') as FormArray;
    }

    get productCarouselFormArray(): FormArray {
        return this.dbcForm.get('productCarousel') as FormArray;
    }

    get socialFormArray(): FormArray {
        return this.dbcForm.get('social') as FormArray;
    }

    // Order form getters
    get orderAddressFormGroup(): FormGroup {
        return this.orderForm.get('address') as FormGroup;
    }

    get orderFormArray(): FormArray {
        return this.orderForm.get('order') as FormArray;
    }

    get frontDesignFormArray(): FormArray {
        return this.orderForm.get('frontDesign') as FormArray;
    }

    get backDesignFormArray(): FormArray {
        return this.orderForm.get('backDesign') as FormArray;
    }

    get addressFormGroup(): FormGroup {
        return this.orderForm.get('address') as FormGroup;
    }

    createBillingAddressFormGroup(): FormGroup {
        return this.fb.group({
            street: ['', Validators.required],
            street1: [''],
            city: ['', Validators.required],
            state: ['', Validators.required],
            zip: ['', Validators.required],
            type: ['Shipping']
        });
    }

    createBackDesignFormGroup(): FormGroup {
        return this.fb.group({
            fileUrl: [''],
            side: ['Back'],
            isTemporary: [false]
        });
    }

    createFrontDesignFormGroup(): FormGroup {
        return this.fb.group({
            fileUrl: [''],
            side: ['Front'],
            isTemporary: [false]
        });
    }

    createOrderFormGroup(): FormGroup {
        return this.fb.group({
            userId: [''],
            totalAmount: [0],
            quantity: [1, [Validators.required, Validators.min(1)]],
            productId: [''],
            status: ['Pending'],
            projectId: ['']
        });
    }

    createPhoneFormGroup(phone?: DbcPhone): FormGroup {
        //need confirmation about the phone/mobile number format
        return this.fb.group({
        type: [phone?.type || 'Mobile', Validators.required],
        number: [phone?.number || '', [Validators.maxLength(20)]]
        });
    }

    createLinkFormGroup(link?: DbcLink): FormGroup {
        return this.fb.group({
            name: [link?.name || '',[Validators.required,Validators.maxLength(100)]],
            url: [link?.url || '',[Validators.required, Validators.pattern('https?://.+')]],
            imageUrl: [link?.imageUrl || '']
        });
    }

    createProductCarouselFormGroup(product?: DbcProductCarousel): FormGroup {
        return this.fb.group({
            name: [product?.name || '', [Validators.required,Validators.maxLength(100)]],
            price: [product?.price || '', [Validators.min(0)]],
            url: [product?.url || '', [Validators.required, Validators.pattern('https?://.+')]],
            imageUrl: [product?.imageUrl || '', [Validators.pattern('https?://.+')]],
            description: [product?.description || '', [Validators.maxLength(500)]]
        });
    }

    createSocialFormGroup(social?: DbcSocial): FormGroup {
        return this.fb.group({
        name: [social?.name || '', Validators.required],
        url: [social?.url || '',[Validators.required, Validators.pattern('https?://.+')]]
        });
    }

    addPhone(phone?: DbcPhone): void {
        this.phoneFormArray.push(this.createPhoneFormGroup(phone));
    }

    addLink(link?: DbcLink): void {
        this.linksFormArray.push(this.createLinkFormGroup(link));
    }

    addProductCarousel(product?: DbcProductCarousel): void {
        this.productCarouselFormArray.push(this.createProductCarouselFormGroup(product));
    }

    addSocial(social?: DbcSocial): void {
        this.socialFormArray.push(this.createSocialFormGroup(social));
    }

    removePhone(index: number): void {
        this.phoneFormArray.removeAt(index);
    }

    removeLink(index: number): void {
        this.linksFormArray.removeAt(index);
    }

    removeProductCarousel(index: number): void {
        this.productCarouselFormArray.removeAt(index);
    }

    removeSocial(index: number): void {
        this.socialFormArray.removeAt(index);
    }

    public resetForm(): void {
        this.dbcForm.reset();

        // Clear arrays
        while (this.phoneFormArray.length > 0) {
            this.phoneFormArray.removeAt(0);
        }

        while (this.linksFormArray.length > 0) {
            this.linksFormArray.removeAt(0);
        }

        while (this.productCarouselFormArray.length > 0) {
            this.productCarouselFormArray.removeAt(0);
        }

        while (this.socialFormArray.length > 0) {
            this.socialFormArray.removeAt(0);
        }

        while (this.defaultContactRecipientsFormArray.length > 0) {
            this.defaultContactRecipientsFormArray.removeAt(0);
        }
    }

    public async getDbcData(projectId: string = ''): Promise<DbcModel[]> {
        const cacheKey = projectId || 'all';

        try {
            // Check cache first
            if (this._dbcCards[cacheKey]) {
                return this._dbcCards[cacheKey];
            }

            const url = `${environment.cognitiveServiceUrl}/DigitalBusinessCard/${projectId}`;
            const response = await firstValueFrom(this.core.get(url));
            const cards = response as DbcModel[];

            // Cache the results
            this._dbcCards[cacheKey] = cards;
            return cards;

        } catch (error) {

            return this.handleApiError(error, 'Failed to load business cards');

        }
    }

    public initializeNewCard(projectId?: string, userId?: string): FormGroup {

        this.isEditMode = false;
        this.currentCardId = null;
        this.currentProjectId = projectId || this.cache.currentProject?.id || null;
        this.setOrgRuleContext(null, null);

        this.resetForm();

        // Set default values for new fields
        this.dbcForm.patchValue({
            projectId: this.currentProjectId ?? '',
            orgId: this.cache.user?.currentOrganizationId ?? '',
            ownerUserId: this.resolveCurrentUserId(),
            organization: this.resolveCurrentOrganizationName(),
            cardMode: '',
            sourceTemplateId: '',
            sourceTemplateVersion: null,
            createdFromOrgTemplate: false,
            teamId: '',
            defaultContactNotifications: {
                emailAlertsEnabled: false,
                emailSubjectTemplate: '',
                emailMessageTemplate: '',
                smsAlertsEnabled: false,
                smsOptInConfirmed: false,
                smsMessageTemplate: ''
            },
            isActive: true,
        });

        // this.addPhone();
        // this.addLink();
        // this.addSocial();

        return this.dbcForm;
    }

    private resolveCurrentOrganizationName(): string {
        return this.cache.organization?.name
            || this.cache.auth?.organization?.name
            || '';
    }

    private resolveCurrentUserId(): string {
        return this.cache.auth?.user?.user?.id ?? '';
    }

    public async initializeExistingCard(cardId: string, projectId?: string): Promise<DbcModel | null> {
        try {
            this.isEditMode = true;
            this.currentCardId = cardId;
            this.currentProjectId = projectId || null;
            const url = `${environment.cognitiveServiceUrl}/DigitalBusinessCard/${cardId}/project/${projectId}`;
            const card = await firstValueFrom(this.core.get(url)) as DbcModel;

            if (card) {
                card.id = cardId;
                this.currentProjectId = card.projectId || projectId || null;

                this.loadCardData(card);
                return card;
            }
            return null;
        } catch (error) {
            this.handleApiError(error, 'Failed to load business card');
            return null;
        }
    }


    public async cardInfo(cardId: string, projectId?: string): Promise<DbcModel | null> {
        try {
            const url = `${environment.cognitiveServiceUrl}/DigitalBusinessCard/${cardId}/project/${projectId}`;
            const card = await firstValueFrom(this.core.get(url)) as DbcModel;
            return card;
        } catch (error) {
            return null;
        }
    }

    private loadCardData(card: DbcModel): void {
        this.utils.logger.info('Loading card data into form:', card);

        this.resetForm();

        if (card.phone && card.phone.length > 0) {
            card.phone.forEach(phone => this.addPhone(phone));
        }

        if (card.links && card.links.length > 0) {
            card.links.forEach(link => this.addLink(link));
        }

        if (card.productCarousel && card.productCarousel.length > 0) {
            card.productCarousel.forEach(product => this.addProductCarousel(product));
        }

        if (card.social && card.social.length > 0) {
            card.social.forEach(social => this.addSocial(social));
        }

        // Add custom forms
        if (card.customForm && card.customForm.length > 0) {
            card.customForm.forEach(form => this.addCustomForm(form));
        }

        this.resetDefaultContactNotifications(card.defaultContactNotifications);

        this.dbcForm.patchValue({

            id: card.id || '',
            projectId: card.projectId || '',
            billingId: card.billingId || this.billingId,
            planName: card.planName || '',
            planType: card.planType || '',
            firstName: card.firstName || '',
            lastName: card.lastName || '',
            userName: card.userName || '',
            email: card.email || '',
            emailForWalletLinks: card.emailForWalletLinks || '',
            logo: card.logo || '',
            photo: card.photo || '',
            appleWalletLogo: (card as any).appleWalletLogo || (card as any).apiLogo || '',
            organization: card.organization || '',
            description: card.description || '',
            title: card.title || '',
            vcf: card.vcf || '',
            qrCode: card.qrCode || '',
            role: card.role || '',
            template: card.template || '',
            primaryColor: card.primaryColor,
            secondaryColor: card.secondaryColor,
            digitalCardTextColor: (card as any).digitalCardTextColor || '#ffffff',
            buttonTextColor: (card as any).buttonTextColor || '#ffffff',
            walletTextColor: (card as any).walletTextColor || '#ffffff',
            walletBackgroundColor: (card as any).walletBackgroundColor || '#873efe',
            includePoweredByUrl: card.includePoweredByUrl,
            poweredBy: card.poweredBy || '',
            poweredByUrl: card.poweredByUrl || '',
            googleWalletSaveUrl: card.googleWalletSaveUrl || '',
            appleWalletPkpassUrl: card.appleWalletPkpassUrl || '',
            backgroundImage: card.backgroundImage || '',
            backgroundImageOpacity: card.backgroundImageOpacity || 0.8,
            customizeWallet: card.customizeWallet,
            brandedQr: card.brandedQr || false,
            defaultContactNotifications: {
                emailAlertsEnabled: card.defaultContactNotifications?.emailAlertsEnabled ?? false,
                emailSubjectTemplate: card.defaultContactNotifications?.emailSubjectTemplate || '',
                emailMessageTemplate: card.defaultContactNotifications?.emailMessageTemplate || '',
                smsAlertsEnabled: card.defaultContactNotifications?.smsAlertsEnabled ?? false,
                smsOptInConfirmed: card.defaultContactNotifications?.smsOptInConfirmed ?? false,
                smsMessageTemplate: card.defaultContactNotifications?.smsMessageTemplate || ''
            },
            webhookUrl: card.webhookUrl || '',
            webhookEnabled: card.webhookEnabled || false,
            orgId: card.orgId ?? '',
            cardMode: card.cardMode ?? '',
            sourceTemplateId: card.sourceTemplateId ?? '',
            sourceTemplateVersion: card.sourceTemplateVersion ?? null,
            createdFromOrgTemplate: card.createdFromOrgTemplate ?? false,
            teamId: card.teamId ?? '',
            isDeleted: card.isDeleted ?? false,
            ownerUserId: card.ownerUserId ?? '',
            isActive: card.isActive ?? false,
        });

    }

    public setOrgRuleContext(effectiveConfig: EffectiveBusinessCardConfig | null, teamId?: string | null): void {
        this.currentEffectiveConfig = effectiveConfig;
        this.currentTeamId = teamId ?? null;

        this.dbcForm.patchValue({
            teamId: this.currentTeamId ?? '',
            sourceTemplateId: effectiveConfig?.appliedTemplateId ?? '',
            sourceTemplateVersion: effectiveConfig?.version ?? null,
            createdFromOrgTemplate: !!effectiveConfig?.isEnabled,
            cardMode: effectiveConfig?.isEnabled ? 'org-template' : ''
        }, { emitEvent: false });

        applyFieldRulesToForm(this.dbcForm, effectiveConfig?.fieldRules);
    }

    public applyEffectiveConfigDefaults(effectiveConfig: EffectiveBusinessCardConfig | null): void {
        if (!effectiveConfig) {
            return;
        }

        const templateDefaults = effectiveConfig.template?.defaults;
        const brandKit = effectiveConfig.brandKit ?? {};

        this.dbcForm.patchValue({
            logo: brandKit.logoUrl || this.dbcForm.get('logo')?.value || '',
            appleWalletLogo: brandKit.appleWalletLogoUrl || this.dbcForm.get('appleWalletLogo')?.value || '',
            backgroundImage: brandKit.backgroundImageUrl || this.dbcForm.get('backgroundImage')?.value || '',
            backgroundImageOpacity: brandKit.backgroundImageOpacity || this.dbcForm.get('backgroundImageOpacity')?.value || '0.8',
            primaryColor: brandKit.primaryColor || this.dbcForm.get('primaryColor')?.value || '',
            secondaryColor: brandKit.secondaryColor || this.dbcForm.get('secondaryColor')?.value || '',
            digitalCardTextColor: brandKit.digitalCardTextColor || this.dbcForm.get('digitalCardTextColor')?.value || '',
            buttonTextColor: brandKit.buttonTextColor || this.dbcForm.get('buttonTextColor')?.value || '',
            walletTextColor: brandKit.walletTextColor || this.dbcForm.get('walletTextColor')?.value || '',
            walletBackgroundColor: brandKit.walletBackgroundColor || this.dbcForm.get('walletBackgroundColor')?.value || '',
            poweredBy: brandKit.poweredBy || this.dbcForm.get('poweredBy')?.value || '',
            poweredByUrl: brandKit.poweredByUrl || this.dbcForm.get('poweredByUrl')?.value || '',
            includePoweredByUrl: brandKit.includePoweredByUrl ?? this.dbcForm.get('includePoweredByUrl')?.value ?? true,
            brandedQr: brandKit.brandedQr ?? this.dbcForm.get('brandedQr')?.value ?? false,
            customizeWallet: brandKit.customizeWallet ?? this.dbcForm.get('customizeWallet')?.value ?? false,
            organization: templateDefaults?.organization || this.dbcForm.get('organization')?.value || '',
            template: effectiveConfig.template?.layoutKey || this.dbcForm.get('template')?.value || ''
        }, { emitEvent: false });

        this.resetAndReplaceArray(this.linksFormArray, templateDefaults?.links ?? [], (item) => this.createLinkFormGroup(item));
        this.resetAndReplaceArray(this.socialFormArray, templateDefaults?.social ?? [], (item) => this.createSocialFormGroup(item as DbcSocial));
        this.resetAndReplaceArray(this.productCarouselFormArray, templateDefaults?.productCarousel ?? [], (item) => this.createProductCarouselFormGroup(item));
        this.resetCustomForms(templateDefaults?.customForm ?? []);
        this.setOrgRuleContext(effectiveConfig, this.currentTeamId);
    }

    public applyLockedEffectiveConfigDefaults(effectiveConfig: EffectiveBusinessCardConfig | null): void {
        if (!effectiveConfig) {
            return;
        }

        const templateDefaults = effectiveConfig.template?.defaults;
        const brandKit = effectiveConfig.brandKit ?? {};
        const fieldRules = effectiveConfig.fieldRules;
        const lockedPatch: Record<string, unknown> = {};

        if (isFieldLocked('logo', fieldRules)) {
            lockedPatch['logo'] = brandKit.logoUrl || this.dbcForm.get('logo')?.value || '';
        }

        if (isFieldLocked('organization', fieldRules)) {
            lockedPatch['organization'] = templateDefaults?.organization || this.dbcForm.get('organization')?.value || '';
        }

        if (isFieldLocked('template', fieldRules)) {
            lockedPatch['template'] = effectiveConfig.template?.layoutKey || this.dbcForm.get('template')?.value || '';
        }

        if (isFieldLocked('colors', fieldRules)) {
            lockedPatch['appleWalletLogo'] = brandKit.appleWalletLogoUrl || this.dbcForm.get('appleWalletLogo')?.value || '';
            lockedPatch['backgroundImage'] = brandKit.backgroundImageUrl || this.dbcForm.get('backgroundImage')?.value || '';
            lockedPatch['backgroundImageOpacity'] = brandKit.backgroundImageOpacity || this.dbcForm.get('backgroundImageOpacity')?.value || '0.8';
            lockedPatch['primaryColor'] = brandKit.primaryColor || this.dbcForm.get('primaryColor')?.value || '';
            lockedPatch['secondaryColor'] = brandKit.secondaryColor || this.dbcForm.get('secondaryColor')?.value || '';
            lockedPatch['digitalCardTextColor'] = brandKit.digitalCardTextColor || this.dbcForm.get('digitalCardTextColor')?.value || '';
            lockedPatch['buttonTextColor'] = brandKit.buttonTextColor || this.dbcForm.get('buttonTextColor')?.value || '';
            lockedPatch['walletTextColor'] = brandKit.walletTextColor || this.dbcForm.get('walletTextColor')?.value || '';
            lockedPatch['walletBackgroundColor'] = brandKit.walletBackgroundColor || this.dbcForm.get('walletBackgroundColor')?.value || '';
            lockedPatch['poweredBy'] = brandKit.poweredBy || this.dbcForm.get('poweredBy')?.value || '';
            lockedPatch['poweredByUrl'] = brandKit.poweredByUrl || this.dbcForm.get('poweredByUrl')?.value || '';
            lockedPatch['includePoweredByUrl'] = brandKit.includePoweredByUrl ?? this.dbcForm.get('includePoweredByUrl')?.value ?? true;
            lockedPatch['brandedQr'] = brandKit.brandedQr ?? this.dbcForm.get('brandedQr')?.value ?? false;
            lockedPatch['customizeWallet'] = brandKit.customizeWallet ?? this.dbcForm.get('customizeWallet')?.value ?? false;
        }

        if (Object.keys(lockedPatch).length > 0) {
            this.dbcForm.patchValue(lockedPatch, { emitEvent: false });
        }

        if (isFieldLocked('links', fieldRules)) {
            this.resetAndReplaceArray(this.linksFormArray, templateDefaults?.links ?? [], (item) => this.createLinkFormGroup(item));
        }

        if (isFieldLocked('social', fieldRules)) {
            this.resetAndReplaceArray(this.socialFormArray, templateDefaults?.social ?? [], (item) => this.createSocialFormGroup(item as DbcSocial));
        }

        if (isFieldLocked('products', fieldRules)) {
            this.resetAndReplaceArray(this.productCarouselFormArray, templateDefaults?.productCarousel ?? [], (item) => this.createProductCarouselFormGroup(item));
        }

        if (isFieldLocked('customForm', fieldRules)) {
            this.resetCustomForms(templateDefaults?.customForm ?? []);
        }

        this.setOrgRuleContext(effectiveConfig, this.currentTeamId);
    }

    private resetAndReplaceArray<T>(formArray: FormArray, items: T[], factory: (item: T) => FormGroup): void {
        while (formArray.length > 0) {
            formArray.removeAt(0);
        }

        items.forEach((item: T) => formArray.push(factory(item)));
    }

    private resetCustomForms(forms: CustomFormConfig[]): void {
        while (this.customForm.length > 0) {
            this.customForm.removeAt(0);
        }

        forms.forEach((form: CustomFormConfig) => this.addCustomForm(form));
    }

    public buildCardInputModel(): DigitalBusinessCardInputModel {
        const formData = this.dbcForm.getRawValue();
        return {
            billingId: formData.billingId,
            firstName: formData.firstName,
            lastName: formData.lastName,
            userName: formData.userName,
            email: formData.email,
            logo: formData.logo,
            photo: formData.photo,
            phone: this.phoneFormArray.value,
            organization: formData.organization,
            title: formData.title,
            links: this.linksFormArray.value,
            social: this.socialFormArray.value,
            role: formData.role,
            template: formData.template,
            primaryColor: formData.primaryColor,
            secondaryColor: formData.secondaryColor,
            description: formData.description,
            poweredBy: formData.poweredBy,
            poweredByUrl: formData.poweredByUrl,
            includePoweredByUrl: formData.includePoweredByUrl,
            backgroundImage: formData.backgroundImage,
            backgroundImageOpacity: formData.backgroundImageOpacity,
            appleWalletLogo: formData.appleWalletLogo,
            customizeWallet: formData.customizeWallet,
            digitalCardTextColor: formData.digitalCardTextColor,
            buttonTextColor: formData.buttonTextColor,
            walletTextColor: formData.walletTextColor,
            walletBackgroundColor: formData.walletBackgroundColor,
            planName: formData.planName,
            brandedQr: formData.brandedQr,
            webhookUrl: formData.webhookUrl,
            dbCardPurchased: formData.dbCardPurchased,
            planType: formData.planType,
            productCarousel: this.productCarouselFormArray.value,
            customForm: this.normalizeCustomFormNotifications(this.customForm.value),
            defaultContactNotifications: this.buildDefaultContactNotificationsModel(),
            isActive: formData.isActive
        };
    }

    private buildStandardCreatePayload(): DbcModel {
        const formData = this.dbcForm.getRawValue();

        return {
            ...formData,
            projectId: formData.projectId || this.cache.currentProject?.id || '',
            orgId: formData.orgId || this.cache.user?.currentOrganizationId || '',
            ownerUserId: formData.ownerUserId || this.cache.userId || '',
            phone: this.phoneFormArray.value,
            links: this.linksFormArray.value,
            social: this.socialFormArray.value,
            productCarousel: this.productCarouselFormArray.value,
            customForm: this.normalizeCustomFormNotifications(this.customForm.value),
            defaultContactNotifications: this.buildDefaultContactNotificationsModel(),
            teamId: formData.teamId || '',
            cardMode: '',
            sourceTemplateId: '',
            sourceTemplateVersion: null,
            createdFromOrgTemplate: false
        };
    }

    public async createCard(): Promise<any> {
        try {
            await this.updateBusinessCardPlanName();
            const formData = this.dbcForm.getRawValue();
            const projectId = formData.projectId || this.cache.currentProject?.id || '';
            const useManagedTemplateCreate = !!this.currentEffectiveConfig?.isEnabled;
            let newFormdData: any;

            if (useManagedTemplateCreate) {
                const orgId = Number(formData.orgId || this.cache.user?.currentOrganizationId || 0);
                const ownerUserId = formData.ownerUserId || this.cache.userId || '';
                const cardInput = this.buildCardInputModel();
                const payload: CreateBusinessCardFromTemplateRequest = buildCreateFromTemplateRequest(
                    orgId,
                    projectId,
                    ownerUserId,
                    formData.teamId || this.currentTeamId,
                    cardInput,
                    this.currentEffectiveConfig
                );

                const url = `${environment.cognitiveServiceUrl}/DigitalBusinessCard/create-from-template`;
                newFormdData = await firstValueFrom(this.core.post(url, payload));
            } else {
                const payload = this.buildStandardCreatePayload();
                const url = `${environment.cognitiveServiceUrl}/DigitalBusinessCard`;
                newFormdData = await firstValueFrom(this.core.post(url, payload));
            }

            // A new card changes both project and manageable-card result sets.
            this._dbcCards = {};

            return {
                success: true,
                id: newFormdData?.id || '',
                projectId: newFormdData?.projectId || ''
            };
        } catch (error) {
            this.handleApiError(error, 'Failed to create business card');
            return false;
        }
    }

    public async updateCard(): Promise<boolean> {
        try {

            if (!this.currentCardId) {
                throw new Error('No card ID found for update');
            }

            await this.updateBusinessCardPlanName();
            const formData = this.dbcForm.getRawValue();
            formData.order = this.orderFormArray.value;
            const sanitizedCardInput = sanitizeCardInputForRules(this.buildCardInputModel(), this.currentEffectiveConfig?.fieldRules);

            // Backward compatibility: mirror appleWalletLogo to legacy apiLogo field if backend still expects it
            if (sanitizedCardInput.appleWalletLogo !== undefined) {
                (sanitizedCardInput as any).apiLogo = sanitizedCardInput.appleWalletLogo;
            }

            if (!formData.projectId) {
                formData.projectId = this.cache.currentProject?.id || '';
            }

            // Ensure isActive is set properly
            formData.isActive = formData.isActive ?? false;

            this.utils.logger.info('isEdit: ', this.isEditMode);
            this.utils.logger.info('card id: ', this.currentCardId);

            const url = `${environment.cognitiveServiceUrl}/DigitalBusinessCard/${this.currentCardId}`;
            const updatePayload: Record<string, unknown> = {
                ...formData,
                ...sanitizedCardInput
            };

            if (this.currentEffectiveConfig?.fieldRules) {
                if (!('phone' in sanitizedCardInput)) delete updatePayload['phone'];
                if (!('links' in sanitizedCardInput)) delete updatePayload['links'];
                if (!('social' in sanitizedCardInput)) delete updatePayload['social'];
                if (!('productCarousel' in sanitizedCardInput)) delete updatePayload['productCarousel'];
                if (!('customForm' in sanitizedCardInput)) delete updatePayload['customForm'];
                if (!('template' in sanitizedCardInput)) delete updatePayload['template'];
                if (!('logo' in sanitizedCardInput)) delete updatePayload['logo'];
                if (!('photo' in sanitizedCardInput)) delete updatePayload['photo'];
                if (!('organization' in sanitizedCardInput)) delete updatePayload['organization'];
                if (!('primaryColor' in sanitizedCardInput)) {
                    delete updatePayload['primaryColor'];
                    delete updatePayload['secondaryColor'];
                    delete updatePayload['digitalCardTextColor'];
                    delete updatePayload['buttonTextColor'];
                    delete updatePayload['walletTextColor'];
                    delete updatePayload['walletBackgroundColor'];
                    delete updatePayload['backgroundImage'];
                    delete updatePayload['backgroundImageOpacity'];
                    delete updatePayload['appleWalletLogo'];
                    delete updatePayload['poweredBy'];
                    delete updatePayload['poweredByUrl'];
                    delete updatePayload['includePoweredByUrl'];
                    delete updatePayload['brandedQr'];
                    delete updatePayload['customizeWallet'];
                }
            }

            const updatedForm = await firstValueFrom(this.core.bulkPut(url, updatePayload));

            // Update the data
            this.dbcForm.patchValue(updatedForm);
            this.utils.logger.info("this.dbcForm.patchValue(updatedForm): ", updatedForm);
            // Ownership or permissions may have changed, so invalidate all card lists.
            this._dbcCards = {};

            return true;
        } catch (error) {
            this.handleApiError(error, 'Failed to update business card');
            return false;
        }
    }

    private async updateBusinessCardPlanName(): Promise<void> {
        const planName = await this.cache.getBusinessCardPlanName();
        this.dbcForm.get('planName')?.setValue(planName);
    }

    public async deleteCard(cardId: string, projectId?: string): Promise<boolean> {
        try {
            const projectIdToUse = projectId || this.currentProjectId || this.cache.currentProject?.id || '';

            const url = `${environment.cognitiveServiceUrl}/DigitalBusinessCard/${cardId}/project/${projectIdToUse}`;
            await firstValueFrom(this.core.delete(url));

            // Clear all cache
            this._dbcCards = {};

            return true;
        } catch (error) {
            this.handleApiError(error, 'Failed to delete business card');
            return false;
        }
    }

    public async saveCard(): Promise<any> {
        try {
            const username = this.dbcForm.get('userName')?.value?.trim();

            if (!username) {
                this.utils.errorMessage(MessageType.TOAST, 'Username cannot be empty');
                return { success: false, error: 'empty_username' };
            }

            if (this.isEditMode) {
                const success = await this.updateCard();
                return { success };
            } else {
                const res = await this.createCard();
                return {
                    success: res.success,
                    id: res.id,
                    projectId: res.projectId
                };
            }
        } catch (error) {
            this.utils.errorMessage(MessageType.TOAST, 'Failed to save business card');
            throw error;
        }
    }

    public async uploadLinkImageUrl(file: File): Promise<any> {
        try {
            const userId = this.cache.userId ?? '';
            const originalName = file.name || '';
            const sanitizedName = originalName.trim().replace(/\s+/g, '_');
            const sanitizedFilename = `${userId}_${crypto.randomUUID()}_${sanitizedName}`;

            const formData = new FormData();
            formData.append('file', file);
            formData.append('name', sanitizedFilename);
            formData.append('folder', 'digital/card/image-button');

            const uploadUrl = `${environment.mediaServiceUrl}/Upload/streaming`;
            this.utils.logger.info('uploadLinkImageUrl() Upload URL:', uploadUrl);

            const response = await firstValueFrom(
                this.core.post(uploadUrl, formData)
            ) as { blobUrl: string };

            this.utils.logger.info('uploadLinkImageUrl upload response:', response);

            if (!response || !response.blobUrl) {
                throw new Error('Media service did not return a valid blob URL');
            }

            return response;
        } catch (error) {
            return this.handleApiError(error, 'Failed to upload logo');
        }
    }

    public async uploadLogo(file: File): Promise<any> {
        try {
            this.utils.logger.info('uploadLogo() Uploading file:', file.name);
            const userId = this.cache.userId ?? '';
            const originalName = file.name || '';
            const sanitizedName = originalName.trim().replace(/\s+/g, '_');
            const sanitizedFilename = `${userId}_${crypto.randomUUID()}_${sanitizedName}`;

            const formData = new FormData();
            formData.append('file', file);
            formData.append('name', sanitizedFilename);
            formData.append('folder', 'digital/card/logo');

            const uploadUrl = `${environment.mediaServiceUrl}/Upload/streaming`;
            this.utils.logger.info('uploadLogo() Upload URL:', uploadUrl);

            const response = await firstValueFrom(
                this.core.post(uploadUrl, formData)
            ) as { blobUrl: string };

            this.utils.logger.info('Logo upload response:', response);

            if (!response || !response.blobUrl) {
                throw new Error('Media service did not return a valid blob URL');
            }

            this.dbcForm.patchValue({
                logo: response.blobUrl
            });

            return response;
        } catch (error) {
            return this.handleApiError(error, 'Failed to upload logo');
        }
    }

    public async uploadPhoto(file: File): Promise<any> {
        try {
            this.utils.logger.info('uploadPhoto() Uploading file:', file.name);
            const userId = this.cache.userId ?? '';
            const originalName = file.name || '';
            const sanitizedName = originalName.trim().replace(/\s+/g, '_');
            const sanitizedFilename = `${userId}_${crypto.randomUUID()}_${sanitizedName}`;

            const formData = new FormData();
            formData.append('file', file);
            formData.append('name', sanitizedFilename);
            formData.append('folder', 'digital/card/photo');

            const uploadUrl = `${environment.mediaServiceUrl}/Upload/streaming`;
            this.utils.logger.info('uploadPhoto() Upload URL:', uploadUrl);

            const response = await firstValueFrom(
                this.core.post(uploadUrl, formData)
            ) as { blobUrl: string };

            this.utils.logger.info('Photo upload response:', response);

            if (!response || !response.blobUrl) {
                throw new Error('Media service did not return a valid blob URL');
            }

            this.dbcForm.patchValue({
                photo: response.blobUrl
            });

            return response;

        } catch (error) {
            return this.handleApiError(error, 'Failed to upload photo');
        }
    }

    public async uploadAppleWalletLogo(file: File): Promise<any> {
        try {
            this.utils.logger.info('uploadAppleWalletLogo() Uploading file:', file.name);
            const userId = this.cache.userId ?? '';
            const originalName = file.name || '';
            const sanitizedName = originalName.trim().replace(/\s+/g, '_');
            const sanitizedFilename = `${userId}_${crypto.randomUUID()}_${sanitizedName}`;

            const formData = new FormData();
            formData.append('file', file);
            formData.append('name', sanitizedFilename);
            formData.append('folder', 'digital/card/apple-wallet-logo');

            const uploadUrl = `${environment.mediaServiceUrl}/Upload/streaming`;
            this.utils.logger.info('uploadAppleWalletLogo() Upload URL:', uploadUrl);

            const response = await firstValueFrom(
                this.core.post(uploadUrl, formData)
            ) as { blobUrl: string };

            this.utils.logger.info('Apple Wallet logo upload response:', response);

            if (!response || !response.blobUrl) {
                throw new Error('Media service did not return a valid blob URL');
            }

            this.dbcForm.patchValue({
                appleWalletLogo: response.blobUrl
            });

            return response;

        } catch (error) {
            return this.handleApiError(error, 'Failed to upload Apple Wallet logo');
        }
    }

    public async uploadBg(file: File): Promise<any> {
        try {
            this.utils.logger.info('uploadBg() Uploading file:', file.name);
            const userId = this.cache.userId ?? '';
            const originalName = file.name || '';
            const sanitizedName = originalName.trim().replace(/\s+/g, '_');
            const sanitizedFilename = `${userId}_${crypto.randomUUID()}_${sanitizedName}`;

            const formData = new FormData();
            formData.append('file', file);
            formData.append('name', sanitizedFilename);
            formData.append('folder', 'digital/card/bg');

            const uploadUrl = `${environment.mediaServiceUrl}/Upload/streaming`;
            this.utils.logger.info('uploadBg() Upload URL:', uploadUrl);

            const response = await firstValueFrom(
                this.core.post(uploadUrl, formData)
            ) as { blobUrl: string };

            this.utils.logger.info('Photo upload response:', response);

            if (!response || !response.blobUrl) {
                throw new Error('Media service did not return a valid blob URL');
            }

            this.dbcForm.patchValue({
                backgroundImage: response.blobUrl
            });

            return response;

        } catch (error) {
            return this.handleApiError(error, 'Failed to upload photo');
        }
    }

    public async uploadCardFrontImage(file: File): Promise<any> {
        try {
            this.utils.logger.info('uploadCardFrontImage() Uploading file:', file.name);
            const userId = this.cache.userId ?? '';
            const originalName = file.name || '';
            const sanitizedName = originalName.trim().replace(/\s+/g, '_');
            const sanitizedFilename = `${userId}_${crypto.randomUUID()}_${sanitizedName}`;

            const formData = new FormData();
            formData.append('file', file);
            formData.append('name', sanitizedFilename);
            formData.append('folder', 'digital/card/custom/front');

            const uploadUrl = `${environment.mediaServiceUrl}/Upload/streaming`;
            this.utils.logger.info('uploadCardFrontImage() Upload URL:', uploadUrl);

            const response = await firstValueFrom(
                this.core.post(uploadUrl, formData)
            ) as { blobUrl: string };

            this.utils.logger.info('Card front image upload response:', response);

            if (!response || !response.blobUrl) {
                throw new Error('Media service did not return a valid blob URL');
            }

            // Ensure front design form array has an entry then set fileUrl
            const frontArray = this.frontDesignFormArray;
            if (frontArray.length === 0) {
                frontArray.push(this.createFrontDesignFormGroup());
            }
            frontArray.at(0)?.get('fileUrl')?.setValue(response.blobUrl);

            return response;
        } catch (error) {
            return this.handleApiError(error, 'Failed to upload front card image');
        }
    }

    public async uploadCardBackImage(file: File): Promise<any> {
        try {
            this.utils.logger.info('uploadCardBackImage() Uploading file:', file.name);
            const userId = this.cache.userId ?? '';
            const originalName = file.name || '';
            const sanitizedName = originalName.trim().replace(/\s+/g, '_');
            const sanitizedFilename = `${userId}_${crypto.randomUUID()}_${sanitizedName}`;

            const formData = new FormData();
            formData.append('file', file);
            formData.append('name', sanitizedFilename);
            formData.append('folder', 'digital/card/custom/back');

            const uploadUrl = `${environment.mediaServiceUrl}/Upload/streaming`;
            this.utils.logger.info('uploadCardBackImage() Upload URL:', uploadUrl);

            const response = await firstValueFrom(
                this.core.post(uploadUrl, formData)
            ) as { blobUrl: string };

            this.utils.logger.info('Card back image upload response:', response);

            if (!response || !response.blobUrl) {
                throw new Error('Media service did not return a valid blob URL');
            }

            // Ensure back design form array has an entry then set fileUrl
            const backArray = this.backDesignFormArray;
            if (backArray.length === 0) {
                backArray.push(this.createBackDesignFormGroup());
            }
            backArray.at(0)?.get('fileUrl')?.setValue(response.blobUrl);

            return response;
        } catch (error) {
            return this.handleApiError(error, 'Failed to upload back card image');
        }
    }

    public async deleteMediaFile(path: string): Promise<any> {
        try {
            this.utils.logger.info('deleteMediaFile() Deleting file:', path);

            const deleteUrl = `${environment.mediaServiceUrl}/Upload?path=${path}`;

            const response = await firstValueFrom(this.core.delete(deleteUrl));

            if (response?.deleted) {
                this.utils.logger.info('File deleted successfully:', response);
            } else {
                this.utils.logger.warn('File deletion response did not confirm deletion:', response);
            }

            return {
                success: !!response?.deleted
            };
        } catch (error) {
            return this.handleApiError(error, 'Failed to delete file');
        }
    }

    public async handleGetProductsAvailable(): Promise<any> {
        this.utils.logger.info('Fetching available products...');
        try {
            const response = this.core.get(`${environment.billingServiceUrl}/GIGProduct`);
            const products = await firstValueFrom(response);
            this._products = Array.isArray(products) ? products : [];
            return this._products || false;
        } catch (error) {
            this.utils.logger.error('Error fetching available products:', error);
            return false;
        }
    }

    public async handleOrderPayment(productId: string): Promise<any> {
        this.utils.logger.info('Processing order payment for product ID:', productId);
        try {
            const response = await this.core.post(`${environment.billingServiceUrl}/Payment/businessCardPayment`, { productId });
            this.utils.logger.info('Order payment response:', response);
            return response || false;
        } catch (error) {
            this.utils.logger.error('Error processing order payment:', error);
            return false;
        }
    }

    public async createOrder(selectedProductId: string): Promise<any> {
        this.utils.logger.info('Creating order for product ID:', selectedProductId);

        const cardId = this.currentCardId ?? this.dbcForm.get('id')?.value ?? '';

        // Validate input parameters
        if (!selectedProductId) {
            throw new Error('Product ID is required');
        }

        try {
            // Validate form before proceeding
            const validationResult = this.validateOrderForm();
            if (!validationResult.isValid) {
                this.utils.errorMessage(MessageType.TOAST, 'Validation Error', validationResult.errorMessage);
                return { success: false, message: validationResult.errorMessage };
            }

            const orderFormData = this.orderForm.getRawValue();
            const userId = this.cache.userId || '';
            const customerId = this.cache.auth?.organization?.stripeCustomerId || this.cache.auth?.organization?.billingId || '';
            const projectId = this.cache.currentProject?.id || '';
            const orgId = Number(this.cache.user?.currentOrganizationId || 0);

            // Additional business logic validation
            if (!userId) {
                throw new Error('User must be logged in to place an order');
            }

            if (!projectId) {
                throw new Error('Project context is required for ordering');
            }

            if (!orgId) {
                throw new Error('Organization context is required for ordering');
            }

            // Validate product exists and get price
            const productPrice = this.getProductPrice(selectedProductId);
            if (productPrice <= 0) {
                throw new Error('Invalid product or pricing information');
            }

            // Build the order payload according to the updated API structure
            const frontDesignBase = this.buildDesignObject('Front', selectedProductId);
            const backDesignBase = this.buildDesignObject('Back', selectedProductId);
            const qrCodeBase = this.buildQrCodeObject();
            const nowIso = new Date().toISOString();

            const customCardId = localStorage.getItem('customCardId') || ''; // value card id
            const isCustom = customCardId === cardId;

            const orderPayload = {
                order: {
                    id: '',
                    userId: userId,
                    customerId: customerId,
                    totalAmount: orderFormData.quantity * productPrice,
                    quantity: orderFormData.quantity || 1,
                    productId: selectedProductId,
                    status: 'Pending',
                    projectId: projectId,
                    digitalCardId: cardId,
                    orgId: orgId,
                    createdDate: nowIso,
                    modifiedDate: nowIso
                },
                frontDesign: {
                    id: '',
                    orderId: '',
                    fileUrl: frontDesignBase?.fileUrl || '',
                    side: frontDesignBase?.side || 'Front',
                    isTemporary: !!frontDesignBase?.isTemporary,
                    createdDate: nowIso,
                    modifiedDate: nowIso
                },
                backDesign: {
                    id: '',
                    orderId: '',
                    fileUrl: backDesignBase?.fileUrl || '',
                    side: backDesignBase?.side || 'Back',
                    isTemporary: !!backDesignBase?.isTemporary,
                    createdDate: nowIso,
                    modifiedDate: nowIso
                },
                qrCode: {
                    id: '',
                    orderId: '',
                    fileUrl: qrCodeBase?.fileUrl || '',
                    side: qrCodeBase?.side || 'QR',
                    isTemporary: !!qrCodeBase?.isTemporary,
                    createdDate: nowIso,
                    modifiedDate: nowIso
                },
                address: {
                    id: 0,
                    street: orderFormData.address?.street || '',
                    street1: orderFormData.address?.street1 || '',
                    city: orderFormData.address?.city || '',
                    state: orderFormData.address?.state || '',
                    zip: orderFormData.address?.zip || '',
                    createdDate: nowIso,
                    modifiedDate: nowIso,
                    orderId: '',
                    type: orderFormData.address?.type || 'Shipping'
                },
                successUrl: `${window.location.origin}/dbc/${cardId}/edit?projectId=${projectId}&custom=${isCustom}&dbc_order_success=true`,
                cancelUrl: `${window.location.origin}/dbc/order-cancelled`,
                providerProductId: this.getProviderProductId(selectedProductId)
            };

            // Remove the old design upload logic since we're using separate frontDesign/backDesign objects now

            this.utils.logger.info('Order payload:', orderPayload);

            // Submit the order to the API
            const response = await firstValueFrom(
                this.core.post(`${environment.billingServiceUrl}/Payment/businessCardPayment`, orderPayload)
            );

            this.utils.logger.info('Order creation response:', response);

            // If order creation is successful and we have a sessionUrl, redirect to it
            if (response && response.sessionUrl) {
                this.utils.logger.info('Redirecting to payment session URL:', response.sessionUrl);
                window.location.href = response.sessionUrl;
            }

            return { success: true, ...response };

        } catch (error) {
            this.utils.logger.error('Error creating order:', error);
            this.handleApiError(error, 'Failed to create order');
            return { success: false, message: 'Failed to create order' };
        }
    }

    private buildDesignObject(side: 'Front' | 'Back', selectedTemplateId?: string): any {
        const designArray = side === 'Front' ? this.frontDesignFormArray : this.backDesignFormArray;
        const design = designArray.value[0];

        // Check if custom design is uploaded
        if (design?.fileUrl) {
            return {
                fileUrl: design.fileUrl,
                side: side,
                isTemporary: design.isTemporary || false
            };
        }

        // Fallback to standard template design URLs
        return this.getStandardDesignUrl(side, selectedTemplateId);
    }

    /**
     * Get the standard template design URL based on selected template and side
     */
    private getStandardDesignUrl(side: 'Front' | 'Back', templateId?: string): any {
        // Default to 'standard' if no template ID provided
        const template = templateId || 'standard';

        // You can customize these URLs based on your actual template structure
        const templateUrls = {
            standard: {
                front: './assets/layout/images/base-card.webp',
                back: './assets/layout/images/base-card.webp'
            },
            custom: {
                front: './assets/layout/images/blank-card.png',
                back: './assets/layout/images/blank-card.png'
            }
            // Add more templates as needed
        };

        // Check if the template name contains 'standard' or 'custom' for dynamic matching
        let selectedTemplate = 'standard'; // default
        if (template && typeof template === 'string') {
            if (template.toLowerCase().includes('standard')) {
                selectedTemplate = 'standard';
            } else if (template.toLowerCase().includes('custom')) {
                selectedTemplate = 'custom';
            }
        }

        // Get URLs for the specific template
        this.utils.logger.info('Loading card tempkate selected by user:', selectedTemplate);
        const templateConfig = templateUrls[selectedTemplate as keyof typeof templateUrls] || templateUrls.standard;
        const fileUrl = side === 'Front' ? templateConfig.front : templateConfig.back;

        return {
            fileUrl: fileUrl,
            side: side,
            isTemporary: false
        };
    }

    /**
     * Build QR code object for order payload using the existing form value 'qrCode'
     */
    private buildQrCodeObject(): { fileUrl: string; side: string; isTemporary: boolean } {
        const qrUrl = this.dbcForm.get('qrCode')?.value || '';
        return {
            fileUrl: qrUrl,
            side: 'QR',
            isTemporary: false
        };
    }

    private getProductPrice(productId: string): number {
        // Find the product by ID and return its price
        const product = this._products.find(p => p.id === productId);
        if (product && product.price) {
            return typeof product.price === 'number' ? product.price : parseFloat(product.price);
        }

        // Fallback to default price if product not found
        this.utils.logger.warn(`Product price not found for ID: ${productId}, using default price`);
        return 10.00;
    }

    /**
     * Get the provider product ID (e.g., Stripe price ID) for a given product
     */
    private getProviderProductId(productId: string): string {
        // Find the product by ID and return its provider product ID
        const product = this._products.find(p => p.id === productId);
        if (product && product.providerProductId) {
            return product.providerProductId;
        }

        // Fallback to a default or the productId itself
        this.utils.logger.warn(`Provider product ID not found for ID: ${productId}, using product ID as fallback`);
        return productId;
    }

    /**
     * Comprehensive validation for order form
     */
    public validateOrderForm(): { isValid: boolean; errorMessage: string } {
        const orderFormData = this.orderForm.getRawValue();

        // Validate quantity
        if (!orderFormData.quantity || orderFormData.quantity < 1) {
            return { isValid: false, errorMessage: 'Quantity must be at least 1' };
        }

        if (orderFormData.quantity > 1000) {
            return { isValid: false, errorMessage: 'Quantity cannot exceed 1000' };
        }

        // Validate address fields

        const address = orderFormData.address;
        if (!address) {
            return { isValid: false, errorMessage: 'Shipping address is required' };
        }

        if (!address.street || address.street.trim() === '') {
            return { isValid: false, errorMessage: 'Street address is required' };
        }

        if (!address.city || address.city.trim() === '') {
            return { isValid: false, errorMessage: 'City is required' };
        }

        if (!address.state || address.state.trim() === '') {
            return { isValid: false, errorMessage: 'State/Province is required' };
        }

        if (!address.zip || address.zip.trim() === '') {
            return { isValid: false, errorMessage: 'ZIP/Postal code is required' };
        }

        // Validate ZIP format - supports US (5 digits + optional 4), Canada (A1A 1A1), Philippines (4 digits)
        const zipPattern = /^[0-9]{5}(-[0-9]{4})?$|^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$|^[0-9]{4}$/;
        if (!zipPattern.test(address.zip.trim())) {
            return { isValid: false, errorMessage: 'Please enter a valid ZIP/Postal code (US: 12345, Canada: K1A 0A6, PH: 1000)' };
        }


        return { isValid: true, errorMessage: '' };
    }

    /**
     * Validate if order can be placed (for component use)
     */
    public canPlaceOrder(selectedProductId?: string): { canPlace: boolean; reason: string } {
        // Check if form is valid using Angular's built-in validation
        if (!this.orderForm.valid) {
            return { canPlace: false, reason: 'Please complete all required fields' };
        }

        // Check if product is selected
        if (!selectedProductId) {
            return { canPlace: false, reason: 'Please select a card template' };
        }

        // Check if product exists and has valid pricing
        const productPrice = this.getProductPrice(selectedProductId);
        if (productPrice <= 0) {
            return { canPlace: false, reason: 'Selected product has invalid pricing' };
        }

        // Check authentication
        if (!this.cache.userId) {
            return { canPlace: false, reason: 'You must be logged in to place an order' };
        }

        // Check project context
        if (!this.cache.currentProject?.id) {
            return { canPlace: false, reason: 'Project context is required' };
        }

        return { canPlace: true, reason: '' };
    }
    // Utility method to handle API errors with specific error codes
    private handleApiError(error: any, defaultMessage: string): never {
        this.utils.logger.error('API Error:', error);

        if (error?.error) {
            const errorData = error.error;

            switch(errorData.code) {
                case 1001:

                    this.utils.errorMessage(
                        MessageType.TOAST,
                        `Digital business card with ID ${errorData.id || 'provided'} not found.`
                    );

                    break;

                case 1002:

                    this.utils.errorMessage(
                        MessageType.TOAST,
                        `Digital business card with username ${errorData.userName || 'provided'} not found.`
                    );

                    break;

                case 1100:

                    this.utils.errorMessage(
                        MessageType.TOAST,
                        `Digital business card with username ${errorData.userName || 'provided'} already exists.`
                    );

                    break;

                default:
                    if (errorData.message) {

                        this.utils.errorMessage(MessageType.TOAST, errorData.message);

                    } else {

                        this.utils.errorMessage(MessageType.TOAST, defaultMessage);

                    }
                    break;
            }
        } else {

            this.utils.errorMessage(MessageType.TOAST, defaultMessage);

        }

        throw error;
    }

    public async addContactForm(
        cardId: string,
        values: Record<string, unknown>,
        sourceFormId?: string | null,
        sourceType: 'default-contact' | 'custom-form' = 'default-contact'
    ): Promise<unknown> {
        const url = `${environment.cognitiveServiceUrl}/DigitalBusinessCard/${encodeURIComponent(cardId)}/submissions`;
        if (!cardId) {
            this.utils.errorMessage(MessageType.TOAST, 'Failed to Submit', 'Card context is missing. Please try again later.');
            this.utils.logger.error('Card ID is required to submit contact form');
            return undefined;
        }

        const cleanedValues = this.cleanValues(values);

        const body: Record<string, unknown> = {
            "values": cleanedValues,
            "sourceType": sourceType
        };
        if (sourceFormId) {
            body.sourceFormId = sourceFormId;
        }
        return await firstValueFrom(this.core.post(url, body));
    }

    public async updateContactForm(cardId: string, formValue: any) {
        const url = `${environment.cognitiveServiceUrl}/DynamicFormResponse`;

        // Trapping here check cardId
        if (!cardId) {
            this.utils.logger.error('Card ID is required to update contact form');
        }

        // orgId stays with the record
        if (!formValue.orgId) {
            const org = this.cache.user?.currentOrganizationId;
            if (org) {
                formValue.orgId = String(org);
            }
        }

        return await firstValueFrom(this.core.bulkPut(url, formValue));
    }

    public async sendToWebhook(url: string, data: any): Promise<any> {
        if (!url) {
            this.utils.logger.error('Webhook URL is required to send data');
            return;
        }

        try {
            // Await the POST request. We don't need the response data, just whether it succeeds.
            const response = await firstValueFrom(this.core.post(url, data));
            this.utils.logger.info('Successfully sent data to webhook.', { url, data, response });
            return response;
        } catch (error) {
            this.utils.logger.error('Failed to send data to webhook.', {
                url,
                error: error instanceof HttpErrorResponse ? { status: error.status, message: error.message } : error
            });
            throw error;
        }
    }

    public async getContactForm(
        cardId: string, // as formId
        pageNumber: number = 1,
        pageSize: number = 20
    ) {
        if (!cardId) {
            this.utils.logger.warn('getContactForm called with empty cardId');
            return null;
        }

        try {
            const organizationId = this.cache.user?.currentOrganizationId;
            const organizationQuery = organizationId
                ? `&organizationId=${encodeURIComponent(String(organizationId))}`
                : '';
            const url = `${environment.cognitiveServiceUrl}/DynamicFormResponse/form/${cardId}?PageNumber=${pageNumber}&PageSize=${pageSize}${organizationQuery}`;
            const response = await firstValueFrom(this.core.get(url));
            return response;

        } catch (error) {
            this.utils.logger.error(`Error fetching contact form:`, error);
            this.handleApiError(error, 'Failed to load contact form');
            return null;
        } finally {
            this.utils.logger.info(`getContactForm finished for cardId: ${cardId}`);
        }
    }

    private cleanValues(values: Record<string, unknown>): Record<string, unknown> {
        return Object.entries(values).reduce<Record<string, unknown>>((cleaned, [key, value]) => {
            cleaned[key.trim()] = this.cleanValue(value);
            return cleaned;
        }, {});
    }

    public async getManageableDbcData(orgId: string | number, force: boolean = false): Promise<DbcModel[]> {
        if (!orgId) {
            return [];
        }

        const cacheKey = `manageable:${orgId}`;
        if (!force && this._dbcCards[cacheKey]) {
            return this._dbcCards[cacheKey];
        }

        try {
            const url = `${environment.cognitiveServiceUrl}/DigitalBusinessCard/org/${orgId}/manageable`;
            const cards = await firstValueFrom(this.core.get(url)) as DbcModel[];
            this._dbcCards[cacheKey] = cards;
            return cards;
        } catch (error) {
            return this.handleApiError(error, 'Failed to load manageable business cards');
        }
    }

    private cleanValue(value: unknown): unknown {
        if (typeof value === 'string') {
            return value.trim();
        }
        if (Array.isArray(value)) {
            return value.map((item: unknown) => this.cleanValue(item));
        }
        if (value !== null && typeof value === 'object') {
            return Object.entries(value as Record<string, unknown>)
                .reduce<Record<string, unknown>>((cleaned, [key, nestedValue]) => {
                    cleaned[key.trim()] = this.cleanValue(nestedValue);
                    return cleaned;
                }, {});
        }
        return value;
    }

    trackView(cardId: string, eventType: string = 'view') {
        return this.sendEvent(cardId, eventType);
    }

    trackSaveContact(cardId: string) {
        return this.sendEvent(cardId, 'download');
    }

    trackAddContactClicked(cardId: string) {
        return this.sendEvent(cardId, 'add-contact-opened');
    }

    trackAddContactSubmitted(cardId: string) {
        return this.sendEvent(cardId, 'contact-saved');
    }

    getSourceFromQuery(): string {
        const params = new URLSearchParams(window.location.search);
        const source = params.get('source') || 'digital-card';
        return source;
    }

    async sendEmail(payload: any): Promise<any> {
        const url = `${environment.notificationUrl}/email`;
        try {
            const response = await firstValueFrom(
                this.http.post(url, payload, { observe: 'response' })
            );

            if (response.status === 200) {
                return {
                    success: true
                }
            } else {
                this.utils.logger.warn(`Email request returned status: ${response.status}`);
            }
        } catch (error) {
            console.error('Error in sendEmail:', error);
            throw error;
        }
        finally {
            this.utils.logger.info('finally sendEmail()');
        }
    }

    private sendEvent(cardId: string, eventType: string) {
        return this.analyticsApiService.captureEvent(cardId, {
            eventType,
            source: this.getSourceFromQuery(),
        });
    }

    public async getCardByUserId(userId: string, orgId?: string | number) {
        this.utils.logger.info('getCardByUserId() Fetching chatbots for user:', userId);

        const resolvedOrgId = orgId ?? this.cache?.user?.currentOrganizationId;

        if(!userId || !resolvedOrgId) {
            this.utils.logger.warn('getCardByUserId() No userId or orgId provided');
            return null;
        }
        try {
            const url = `${environment.cognitiveServiceUrl}/DigitalBusinessCard/org/${resolvedOrgId}/user/${userId}`;
            this.utils.logger.info('getCardByUserId() API URL:', url);
            const data = await firstValueFrom(this.core.get(url));
            return data;
        } catch (error) {
            this.utils.logger.error('getCardByUserId() API error details:', error);
            this.handleApiError(error, 'Failed to load user chatbots');
            return null;
        }
    }

    public async getCardByOrgId(id: number | string) {
        this.utils.logger.info('getCardByOrgId() Fetching chatbots for organization:', id);

        const orgId = id ?? this.cache?.user?.currentOrganizationId;

        if(!orgId) {
            this.utils.logger.warn('getCardByOrgId() No orgId provided');
            return null;
        }
        try {
            const url = `${environment.cognitiveServiceUrl}/DigitalBusinessCard/org/${orgId}/count`;
            this.utils.logger.info('getCardByOrgId() API URL:', url);
            const data = await firstValueFrom(this.core.get(url));
            return data;
        } catch (error) {
            this.utils.logger.error('getCardByOrgId() API error details:', error);
            this.handleApiError(error, 'Failed to load organization chatbots');
            return null;
        }
    }

    /**
     * Get order history for a specific project
     * @param projectId - The project ID to fetch orders for
     * @param status - Optional status filter
     * @param pageNumber - Optional page number for pagination
     * @param pageSize - Optional page size for pagination
     * @returns Promise<any[]> - Array of order objects
     */
    public async getOrderHistory(projectId: string, status?: string, pageNumber?: number, pageSize?: number): Promise<HistoryItem[]> {
        try {
            this.utils.logger.info('Fetching order history for project:', projectId);

            // Build query parameters
            const params = new URLSearchParams();

            if (status && status.trim() !== '') {
                params.append('status', status);
            }

            if (pageNumber !== undefined && pageNumber > 0) {
                params.append('PageNumber', pageNumber.toString());
            }

            if (pageSize !== undefined && pageSize > 0) {
                params.append('PageSize', pageSize.toString());
            }

            // Construct the URL with query parameters
            const baseUrl = `${environment.billingServiceUrl}/Order/projectId/${projectId}`;
            const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

            this.utils.logger.info('Order history API URL:', url);

            const response = await firstValueFrom(this.core.get(url));
            const orders = Array.isArray(response) ? response : [];

            this.utils.logger.info('Order history response:', orders);
            return orders;

        } catch (error) {
            this.utils.logger.error('Error fetching order history:', error);
            this.handleApiError(error, 'Failed to load order history');
            return [];
        }
    }

    /**
     * Get physical business-card orders placed by any member of an organization.
     */
    public async getOrganizationOrderHistory(orgId: string | number, pageNumber: number = 1, pageSize: number = 1000): Promise<HistoryItem[]> {
        if (!orgId) {
            this.utils.logger.warn('getOrganizationOrderHistory() No organization ID provided');
            return [];
        }

        try {
            const params = new URLSearchParams({
                PageNumber: pageNumber.toString(),
                PageSize: pageSize.toString()
            });
            const url = `${environment.billingServiceUrl}/Order/organization/${orgId}?${params.toString()}`;
            const response = await firstValueFrom(this.core.get(url));
            return Array.isArray(response) ? response as HistoryItem[] : [];
        } catch (error) {
            this.utils.logger.error('Error fetching organization order history:', error);
            this.handleApiError(error, 'Failed to load organization order history');
            return [];
        }
    }

    /**
     * Helper method to get order history with pagination info
     * @param projectId - The project ID to fetch orders for
     * @param options - Options object containing status, pageNumber, and pageSize
     * @returns Promise with orders and pagination metadata
     */
    public async getOrderHistoryWithPagination(
        projectId: string,
        options: {
            status?: string;
            pageNumber?: number;
            pageSize?: number;
        } = {}
    ): Promise<OrderHistoryResponse> {
        try {
            const { status, pageNumber = 1, pageSize = 10 } = options;

            const orders = await this.getOrderHistory(projectId, status, pageNumber, pageSize);

            // If the API returns pagination metadata in headers or response body, extract it here
            // For now, we'll return the orders with basic pagination info
            return {
                orders: orders,
                totalCount: orders.length, // This should come from API response headers or body
                pageNumber: pageNumber,
                pageSize: pageSize
            };

        } catch (error) {
            this.utils.logger.error('Error fetching paginated order history:', error);
            return {
                orders: [],
                totalCount: 0,
                pageNumber: 1,
                pageSize: 10
            };
        }
    }

    /**
     * Get order details by order ID
     * @param orderId - The order ID to fetch details for
     * @param projectId - Optional project ID for additional context
     * @returns Promise<any> - Order details object
     */
    public async getOrderDetails(orderId: string, projectId?: string): Promise<any> {
        try {
            this.utils.logger.info('Fetching order details for ID:', orderId);

            const url = `${environment.billingServiceUrl}/api/v1/Order/${orderId}`;

            this.utils.logger.info('Order details API URL:', url);

            const response = await firstValueFrom(this.core.get(url));

            this.utils.logger.info('Order details response:', response);
            return response;

        } catch (error) {
            this.utils.logger.error('Error fetching order details:', error);
            this.handleApiError(error, 'Failed to load order details');
            return null;
        }
    }

    /**
     * Download order invoice
     * @param orderId - The order ID to download invoice for
     * @returns Promise<Blob> - Invoice file blob
     */
    public async downloadOrderInvoice(orderId: string): Promise<Blob | null> {
        try {
            this.utils.logger.info('Downloading invoice for order ID:', orderId);

            const url = `${environment.billingServiceUrl}/Order/${orderId}/invoice`;

            this.utils.logger.info('Invoice download API URL:', url);

            // Use injected HTTP client directly for blob response
            const response = await firstValueFrom(
                this.http.get(url, { responseType: 'blob' })
            );

            this.utils.logger.info('Invoice download successful');
            return response;

        } catch (error) {
            this.utils.logger.error('Error downloading invoice:', error);
            this.utils.errorMessage(MessageType.TOAST, 'Failed to download invoice');
            return null;
        }
    }

    /**
     * Update order status
     * @param orderId - The order ID to update
     * @param newStatus - The new status to set
     * @param projectId - Optional project ID for context
     * @returns Promise<boolean> - Success status
     */
    public async updateOrderStatus(orderId: string, newStatus: string, projectId?: string): Promise<boolean> {
        try {
            this.utils.logger.info('Updating order status:', { orderId, newStatus, projectId });

            const url = `${environment.billingServiceUrl}/api/v1/Order/${orderId}/status`;

            const payload = {
                status: newStatus,
                projectId: projectId
            };

            await firstValueFrom(this.core.put(url, payload));

            this.utils.logger.info('Order status updated successfully');
            this.utils.successMessage(MessageType.TOAST, 'Order status updated successfully');
            return true;

        } catch (error) {
            this.utils.logger.error('Error updating order status:', error);
            this.handleApiError(error, 'Failed to update order status');
            return false;
        }
    }

    /**
     * Cancel an order
     * @param orderId - The order ID to cancel
     * @param reason - Optional cancellation reason
     * @returns Promise<boolean> - Success status
     */
    public async cancelOrder(orderId: string, reason?: string): Promise<boolean> {
        try {
            this.utils.logger.info('Cancelling order:', { orderId, reason });

            const url = `${environment.billingServiceUrl}/api/v1/Order/${orderId}/cancel`;

            const payload = {
                reason: reason || 'Customer requested cancellation'
            };

            await firstValueFrom(this.core.post(url, payload));

            this.utils.logger.info('Order cancelled successfully');
            this.utils.successMessage(MessageType.TOAST, 'Order cancelled successfully');
            return true;

        } catch (error) {
            this.utils.logger.error('Error cancelling order:', error);
            this.handleApiError(error, 'Failed to cancel order');
            return false;
        }
    }

    // Custom Forms methods
    private addCustomForm(form: Partial<CustomFormConfig> = {}): FormGroup {
        const formGroup = this.fb.group({
            name: [form.name || '', Validators.required],
            slug: [form.slug || ''],
            formId: [form.formId || ''],
            customFields: this.fb.array([]),
            sendResultsTo: this.fb.array([]),
            emailAlertsEnabled: [form.emailAlertsEnabled ?? false],
            emailSubjectTemplate: [form.emailSubjectTemplate || ''],
            emailMessageTemplate: [form.emailMessageTemplate || ''],
            smsAlertsEnabled: [form.smsAlertsEnabled ?? false],
            smsOptInConfirmed: [form.smsOptInConfirmed ?? false],
            smsMessageTemplate: [form.smsMessageTemplate || '']
        });

        this.customForm.push(formGroup);

        if (form.customFields) {
            form.customFields.forEach(field => this.addCustomFormField(formGroup, field));
        }

        if (form.sendResultsTo) {
            form.sendResultsTo.forEach(recipient => this.addCustomFormRecipient(formGroup, recipient));
        }

        return formGroup;
    }

    private addCustomFormField(formGroup: FormGroup, field: Partial<CustomField> = {}): void {
        const fieldsArray = formGroup.get('customFields') as FormArray;

        let optionControls: FormGroup[] = [];

        if (field.options && field.options.length > 0 && field.type === 'dropdown') {
            optionControls = field.options.map(option => {
                return this.fb.group({
                    key: [option.key || '', Validators.required],
                    value: [option.value || '', Validators.required]
                });
            });
        }

        const optionsArray = this.fb.array(optionControls);

        const customField = this.fb.group({
            name: [field.name || '', Validators.required],
            label: [field.label || ''],
            placeholder: [field.placeholder || ''],
            required: [typeof field.required === 'boolean' ? String(field.required) : (field.required || 'false')],
            type: [field.type || '', Validators.required],
            options: optionsArray
        });

        fieldsArray.push(customField);
    }

    private addCustomFormRecipient(formGroup: FormGroup, recipient: Partial<Recipient> = {}): void {
        const recipientsArray = formGroup.get('sendResultsTo') as FormArray;
        recipientsArray.push(this.createRecipientFormGroup(recipient));
    }

    clearCustomForm(): void {
        this.customForm.clear();
    }

    public addDefaultContactRecipient(recipient: Partial<Recipient> = {}): void {
        this.defaultContactRecipientsFormArray.push(this.createRecipientFormGroup(recipient));
    }

    public removeDefaultContactRecipient(index: number): void {
        this.defaultContactRecipientsFormArray.removeAt(index);
    }

    private createDefaultContactNotificationsGroup(
        config: Partial<DefaultContactNotificationsConfig> = {}
    ): FormGroup {
        const group = this.fb.group({
            emailAlertsEnabled: [config.emailAlertsEnabled ?? false],
            emailSubjectTemplate: [config.emailSubjectTemplate || ''],
            emailMessageTemplate: [config.emailMessageTemplate || ''],
            smsAlertsEnabled: [config.smsAlertsEnabled ?? false],
            smsOptInConfirmed: [config.smsOptInConfirmed ?? false],
            smsMessageTemplate: [config.smsMessageTemplate || ''],
            sendResultsTo: this.fb.array([])
        });

        config.sendResultsTo?.forEach((recipient: Recipient) => {
            (group.get('sendResultsTo') as FormArray).push(this.createRecipientFormGroup(recipient));
        });

        return group;
    }

    private createRecipientFormGroup(recipient: Partial<Recipient> = {}): FormGroup {
        return this.fb.group({
            name: [recipient.name || '', Validators.required],
            type: [recipient.type || 'email', Validators.required],
            useWebhookResultInToolResponse: [recipient.useWebhookResultInToolResponse || false]
        });
    }

    private resetDefaultContactNotifications(config?: DefaultContactNotificationsConfig): void {
        while (this.defaultContactRecipientsFormArray.length > 0) {
            this.defaultContactRecipientsFormArray.removeAt(0);
        }

        config?.sendResultsTo?.forEach((recipient: Recipient) => this.addDefaultContactRecipient(recipient));
    }

    private buildDefaultContactNotificationsModel(): DefaultContactNotificationsConfig {
        const formValue = this.defaultContactNotificationsGroup.getRawValue();
        return {
            emailAlertsEnabled: !!formValue.emailAlertsEnabled,
            emailSubjectTemplate: formValue.emailSubjectTemplate || '',
            emailMessageTemplate: formValue.emailMessageTemplate || '',
            smsAlertsEnabled: !!formValue.smsAlertsEnabled,
            smsOptInConfirmed: !!formValue.smsOptInConfirmed,
            smsMessageTemplate: formValue.smsMessageTemplate || '',
            sendResultsTo: this.normalizeRecipientsForNotifications(this.defaultContactRecipientsFormArray.value)
        };
    }

    private normalizeCustomFormNotifications(forms: CustomFormConfig[]): CustomFormConfig[] {
        return (forms || []).map((form: CustomFormConfig) => ({
            ...form,
            emailAlertsEnabled: !!form.emailAlertsEnabled,
            emailSubjectTemplate: form.emailSubjectTemplate || '',
            emailMessageTemplate: form.emailMessageTemplate || '',
            smsAlertsEnabled: !!form.smsAlertsEnabled,
            smsOptInConfirmed: !!form.smsOptInConfirmed,
            smsMessageTemplate: form.smsMessageTemplate || '',
            sendResultsTo: this.normalizeRecipientsForNotifications(form.sendResultsTo || [])
        }));
    }

    private normalizeRecipientsForNotifications(recipients: Recipient[]): Recipient[] {
        return (recipients || []).map((recipient: Recipient) => ({
            ...recipient,
            name: recipient.type === 'sms'
                ? this.normalizeSmsDestination(recipient.name)
                : (recipient.name || '').trim()
        }));
    }

    private normalizeSmsDestination(value: string): string {
        const trimmedValue = (value || '').trim();
        if (!trimmedValue) {
            return '';
        }

        const hasLeadingPlus = trimmedValue.startsWith('+');
        const digitsOnly = trimmedValue.replace(/\D/g, '');

        if (hasLeadingPlus && DbcService.SMS_DESTINATION_REGEX.test(`+${digitsOnly}`)) {
            return `+${digitsOnly}`;
        }

        if (digitsOnly.length === 10) {
            return `+1${digitsOnly}`;
        }

        if (digitsOnly.length === 11 && digitsOnly.startsWith('1')) {
            return `+${digitsOnly}`;
        }

        return trimmedValue;
    }

}
